// /welcome — self-onboarding for the alpha distribution flow. The teacher
// shares one /welcome URL with the class; each kid types a name and we mint
// an account on the spot (POST /api/auth/quick-signup) + set the session
// cookie. After signup we surface their personal short URL so a parent can
// screenshot it for next-time access from another device.
//
// Diverges from spec's "5–10 hardcoded student accounts + paper sticker" —
// real distribution is one URL groupcast with unknown attendance, so the
// signup has to happen on the page, not ahead of time. See v0.1 validation
// report for the why; alpha data is wiped between cohorts.

import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMe, useQuickSignup } from '../auth/hooks.ts'

type Stage =
  | { kind: 'form' }
  | { kind: 'success'; displayName: string; shortToken: string }

export function WelcomePage() {
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement | null>(null)
  // Latches the moment our own signup populated the me query. Without it the
  // "kick already-signed-in visitors to /" effect fires immediately after
  // success (useQuickSignup writes the new user into the me cache) and the
  // success view never gets to render.
  const justSignedUpRef = useRef(false)
  const meQuery = useMe()
  const { mutate, isPending, error } = useQuickSignup()
  const [name, setName] = useState('')
  const [stage, setStage] = useState<Stage>({ kind: 'form' })

  // If someone with an existing session lands on /welcome, kick to home
  // rather than letting them mint a second account on top of their own.
  useEffect(() => {
    if (justSignedUpRef.current) return
    if (meQuery.data) {
      navigate('/', { replace: true })
    }
  }, [meQuery.data, navigate])

  useEffect(() => {
    if (stage.kind === 'form') {
      inputRef.current?.focus()
    }
  }, [stage.kind])

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    const trimmed = name.trim()
    if (!trimmed) return
    mutate(trimmed, {
      onSuccess: ({ user, shortToken }) => {
        justSignedUpRef.current = true
        setStage({ kind: 'success', displayName: user.displayName, shortToken })
      },
    })
  }

  if (stage.kind === 'success') {
    return <SuccessView displayName={stage.displayName} shortToken={stage.shortToken} />
  }

  const ready = name.trim().length > 0

  return (
    <main className="mx-auto flex min-h-full max-w-md flex-col justify-center gap-6 p-8">
      <header className="text-center">
        <h1 className="font-[family-name:var(--font-display)] text-4xl text-[var(--color-joseph-primary)]">
          Skena
        </h1>
        <p className="mt-2 text-sm text-[var(--color-muted)]">
          歡迎來玩 ✦ 認識聖經、邊上色
        </p>
      </header>

      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <label className="flex flex-col gap-2 text-sm">
          <span className="text-[var(--color-muted)]">你叫什麼名字？</span>
          <input
            ref={inputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={40}
            autoCapitalize="words"
            autoCorrect="off"
            className="rounded-[var(--radius-soft)] border border-[var(--color-muted)]/30 bg-white px-4 py-3 text-xl focus:border-[var(--color-joseph-primary)] focus:outline-none"
            placeholder="小強"
            aria-label="你的名字"
          />
        </label>

        {error ? (
          <p className="text-sm text-amber-700" role="alert">
            {error.code === 'VALIDATION_ERROR'
              ? '名字格式不對，請再試一次'
              : '出了點問題，等一下再試試'}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={!ready || isPending}
          className="rounded-[var(--radius-pill)] bg-[var(--color-joseph-primary)] px-6 py-4 text-lg font-semibold text-white shadow-md disabled:opacity-50"
        >
          {isPending ? '進入中…' : '✦ 開始'}
        </button>
      </form>

      <p className="text-center text-xs text-[var(--color-muted)]">
        忘記你的個人連結？
        <Link to="/login" className="ml-1 underline-offset-4 hover:underline">
          用班級代碼登入
        </Link>
      </p>
    </main>
  )
}

function SuccessView({
  displayName,
  shortToken,
}: {
  displayName: string
  shortToken: string
}) {
  const personalUrl = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    return `${origin}/k/${shortToken}`
  }, [shortToken])
  const [copied, setCopied] = useState(false)

  const onCopy = async () => {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return
    try {
      await navigator.clipboard.writeText(personalUrl)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Some browsers reject silent clipboard writes; user can still long-press to copy.
    }
  }

  return (
    <main className="mx-auto flex min-h-full max-w-md flex-col justify-center gap-6 p-8">
      <header className="text-center">
        <p className="text-sm tracking-wider uppercase text-[var(--color-joseph-primary)]">
          建立完成
        </p>
        <h1 className="mt-1 font-[family-name:var(--font-display)] text-3xl text-[var(--color-fg)]">
          歡迎，{displayName}！
        </h1>
      </header>

      <section className="flex flex-col gap-3 rounded-[var(--radius-soft)] border border-[var(--color-joseph-primary)]/30 bg-white/80 p-5">
        <p className="text-sm text-[var(--color-fg)]">
          告訴爸爸媽媽記下這個連結。下次點它，你就直接回到 Skena。
        </p>
        <code className="break-all rounded-[6px] bg-black/5 px-3 py-2 text-sm text-[var(--color-fg)]">
          {personalUrl}
        </code>
        <button
          type="button"
          onClick={onCopy}
          className="self-start rounded-[var(--radius-pill)] border border-[var(--color-joseph-primary)] px-4 py-2 text-sm font-semibold text-[var(--color-joseph-primary)]"
        >
          {copied ? '已複製 ✓' : '複製連結給家長'}
        </button>
      </section>

      <Link
        to="/"
        replace
        className="rounded-[var(--radius-pill)] bg-[var(--color-joseph-primary)] px-6 py-4 text-center text-lg font-semibold text-white shadow-md"
      >
        ✦ 開始玩
      </Link>
    </main>
  )
}
